import { merge } from '@art-suite/art-core-ts';
import { optional, optionalBoolean, optionalString, string, zodLess, ZodLessInfer, zrecord } from '@kaiizen/zod-less';
import { isReservedOrgAlias, ORG_ALIAS_MAX_LENGTH } from '../../ApiLib/getPropertyIdLabel';
import { ApiTaskSettingsModifiersSchema } from './ApiTaskSettingsModifiersSchema';
import { ApiUserOrgRoleSchema } from './ApiUserOrgSchema';

export { ApiTaskSettingsModifiersSchema, TaskModifierTypeSchema } from './ApiTaskSettingsModifiersSchema';
export type { ApiTaskSettingsModifiers, TaskModifierType } from './ApiTaskSettingsModifiersSchema';

export const ApiTradeSettingsTradeSchema = zodLess({
  displayName: optionalString,
  category: optionalString,
  description: optionalString,
  isEnabled: optionalBoolean,
});
export type ApiTradeSettingsTrade = ZodLessInfer<typeof ApiTradeSettingsTradeSchema>;

const ApiOrgAliasSchema = string
  .trim()
  .max(ORG_ALIAS_MAX_LENGTH)
  .refine(value => value.length === 0 || !isReservedOrgAlias(value), {
    message: 'Organization alias cannot be Kai.',
  });

/**
 * ApiAllOrgSettings — the unified, strongly-typed org settings object.
 *
 * ## Structure: key → props → values
 *
 * Org settings are organized in a three-level hierarchy:
 *
 *   1. **Key** — a top-level setting category (e.g. `exportSettings`, `taskSettings`).
 *      Each key corresponds to a separate database record keyed by the OrgSettingsKey enum.
 *   2. **Props** — named properties within a key (e.g. `excludePageNumbers`, `modifiers`).
 *   3. **Values** — the value of each prop (boolean, string, object, etc.).
 *
 * ## Defaults = absence
 *
 * The default value for any setting is `undefined` / absent. A blank `{}` for a given
 * key implies fully default behavior — there is no need to store or transmit defaults.
 *
 * All booleans are oriented so that `false` (or absent) = default behavior and
 * `true` = override. For example, page numbers are included by default, so the
 * setting is `excludePageNumbers` (opt-in to override), not `includePageNumbers`.
 *
 * ## Merge semantics (PATCH behavior)
 *
 * When updating, you send one or more keys with whichever props you want to change:
 *
 *   - **Keys** you don't include are left untouched.
 *   - **Props** you don't include within a key are left untouched.
 *   - **Values** of props you do include are fully replaced, not deep-merged.
 *
 * This means if a prop's value is an object (e.g. the `modifiers` record in
 * `taskSettings`), you must send the complete object — it will replace the
 * existing value entirely.
 *
 * ## Extending org settings
 *
 * To add a new setting category:
 *
 *   1. Add the new key to the `OrgSettingsKey` enum in the Prisma schema
 *      and create a database migration (`ALTER TYPE "OrgSettingsKey" ADD VALUE '...'`).
 *   2. Add the new key and its props to this schema below.
 *   3. Orient booleans so `false`/absent = default. Name them for the override
 *      behavior (e.g. `excludeX`, `hideY`, `requireZ`), not the default.
 *   4. Keep UI-specific strings (help text, descriptions, labels) out of settings
 *      data. Settings store configuration values only; display concerns belong
 *      in the UX layer.
 */
export const ApiAllOrgSettingsSchema = zodLess({
  exportSettings: optional({
    excludePageNumbers: optionalBoolean,
    excludeTaskPhotos: optionalBoolean,
  }),
  taskSettings: optional({
    modifiers: optional(zrecord(ApiTaskSettingsModifiersSchema)),
  }),
  brandSettings: optional({
    logoSvg: optionalString,
    orgAlias: optional(ApiOrgAliasSchema),
  }),
  projectSettings: optional({
    types: optional(zrecord({
      displayName: optionalString,
      description: optionalString,
      isEnabled: optionalBoolean,
    })),
  }),
  tradeSettings: optional({
    // key = canonical trade slug.
    // Values are org-specific overrides for label/description plus disabled state.
    // Unknown keys are ignored and omitted keys reset to the standard trade defaults.
    trades: optional(zrecord(ApiTradeSettingsTradeSchema)),
  }),
  notificationSettings: optional({
    // key = NotificationKey from NotificationRegistry.
    // Absence = use catalog defaults for that key.
    // Presence of a prop replaces that prop's default; absent props fall back to catalog defaults.
    overrides: optional(zrecord(zodLess({
      enabled: optionalBoolean,
      emailEnabled: optionalBoolean,
      recipientRoles: optional([ApiUserOrgRoleSchema]),
    }))),
  }),
  // FUTURE
  // financialSettings: optional({
  //   hideLinePricesFromFieldUsers: optionalBoolean,
  //   hideProjectTotalFromFieldUsers: optionalBoolean,
  // }),
  // workCategorySettings: optional({
  //   categories: optional(zrecord({
  //     glCode: optionalString,
  //     description: optionalString,
  //   })),
  // }),
});
export type ApiAllOrgSettings = ZodLessInfer<typeof ApiAllOrgSettingsSchema>;
export const ApiAllOrgSettingsKeysSchema = ApiAllOrgSettingsSchema.keyof();
export type ApiAllOrgSettingsKeys = ZodLessInfer<typeof ApiAllOrgSettingsKeysSchema>;

/**
 * Merge two ApiAllOrgSettings objects (key → props → values).
 *
 * Keys and props are merged: anything not present in `settings2` is preserved
 * from `settings1`. Prop values from `settings2` fully replace those in `settings1`.
 */
export const mergeAllOrgSettings = (settings1: ApiAllOrgSettings, settings2: ApiAllOrgSettings): ApiAllOrgSettings => {
  const uniqueKeys: Set<(keyof ApiAllOrgSettings)> = new Set<(keyof ApiAllOrgSettings)>(
    [...Object.keys(settings1), ...Object.keys(settings2)] as (keyof ApiAllOrgSettings)[],
  );
  const mergedSettings: ApiAllOrgSettings = {};
  for (const key of uniqueKeys) {
    mergedSettings[key] = merge(settings1[key], settings2[key]);
  }
  return mergedSettings;
};
